import { create } from 'zustand';
import type { WallAnalysisRequest, WallFloor } from './types';
import type { SeismicInput } from '../beams/types';

interface State {
  request: WallAnalysisRequest;
  setGeometry: (patch: Partial<WallAnalysisRequest['geometry']>) => void;
  setAutoLoads: (patch: Partial<WallAnalysisRequest['auto_loads']>) => void;
  addFloor: () => void;
  updateFloor: (idx: number, patch: Partial<WallFloor>) => void;
  removeFloor: (idx: number) => void;
  setSeismic: (patch: Partial<SeismicInput>) => void;
  setStiffness: (v: number) => void;
  setMaterials: (patch: Partial<Pick<WallAnalysisRequest, 'fc' | 'fy' | 'cover_cm' | 'combo'>>) => void;
  loadRequest: (r: WallAnalysisRequest) => void;
}

const defaultSeismic: SeismicInput = {
  zone: 'Z4',
  usage: 'C',
  soil: 'S2',
  system: 'muros_concreto',
  building_height_m: 12.0,
  seismic_weight_kgf: 800_000,
  floor_shear_fraction: 1.0,
  beam_shear_fraction: 1.0,
};

const defaults: WallAnalysisRequest = {
  geometry: { lw_m: 3.0, tw_cm: 25, hw_m: 12.0 },
  auto_loads: {
    tributary_area_m2: 25,
    floors: Array.from({ length: 4 }, () => ({ use: 'vivienda' as const, height_m: 3.0 })),
    slab_dead_load_kgf_m2: 500,
  },
  seismic: defaultSeismic,
  wall_stiffness_ratio: 0.5,
  combo: 'E060',
  fc: 210,
  fy: 4200,
  cover_cm: 4,
};

export const useWallAnalysisStore = create<State>((set) => ({
  request: defaults,
  setGeometry: (patch) =>
    set((s) => ({ request: { ...s.request, geometry: { ...s.request.geometry, ...patch } } })),
  setAutoLoads: (patch) =>
    set((s) => ({ request: { ...s.request, auto_loads: { ...s.request.auto_loads, ...patch } } })),
  addFloor: () =>
    set((s) => ({
      request: {
        ...s.request,
        auto_loads: {
          ...s.request.auto_loads,
          floors: [...s.request.auto_loads.floors, { use: 'vivienda', height_m: 3.0 }],
        },
      },
    })),
  updateFloor: (idx, patch) =>
    set((s) => ({
      request: {
        ...s.request,
        auto_loads: {
          ...s.request.auto_loads,
          floors: s.request.auto_loads.floors.map((f, i) => (i === idx ? { ...f, ...patch } : f)),
        },
      },
    })),
  removeFloor: (idx) =>
    set((s) => ({
      request: {
        ...s.request,
        auto_loads: {
          ...s.request.auto_loads,
          floors: s.request.auto_loads.floors.filter((_, i) => i !== idx),
        },
      },
    })),
  setSeismic: (patch) =>
    set((s) => ({ request: { ...s.request, seismic: { ...s.request.seismic, ...patch } } })),
  setStiffness: (v) => set((s) => ({ request: { ...s.request, wall_stiffness_ratio: v } })),
  setMaterials: (patch) => set((s) => ({ request: { ...s.request, ...patch } })),
  loadRequest: (r) => set({ request: r }),
}));
